import { getMode, getSceneFiltersForElement, isModeName } from "@/modes";
import { ModeNames } from "@/modes/mode";
import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import { setCompatibleSceneFilterForMode } from "@/store/mode-thunks";
import { selectActiveElementId } from "@/store/selections-selectors";
import { setActiveElement } from "@/store/selections-slice";
import { selectIElement } from "@faro-lotv/project-source";
import type { SavedViewData } from "@faro-lotv/sphere-viewer-contracts";
import { TrackedState } from "./tracked-state";

/** The active element and mode, as a subset of a saved view */
export type ElementAndModeEntry = Required<Pick<SavedViewData, "id">> & {
  /** The active mode */
  mode: ModeNames;
};

/**
 * @param mode The mode to check
 * @returns whether the user navigates the project in this mode, as opposed to
 * booting the app or running an exclusive workflow like an alignment
 */
export function isViewingMode(mode: ModeNames): boolean {
  return mode !== "start" && !getMode(mode).exclusive;
}

/**
 * Tracks the active element together with the mode: whether an element can be
 * shown depends on the mode, and changing the mode can correct the element, so
 * the two are validated and restored as one.
 *
 * An entry can be restored if its element still exists and its mode is a viewing
 * mode that can be started with that element. Restoring also picks a scene filter
 * the element is visible in.
 */
export const elementAndMode: TrackedState<ElementAndModeEntry> = {
  /**
   * @param state The current state
   * @returns the active element and mode, or undefined without an active element
   */
  capture(state) {
    const id = selectActiveElementId(state);
    return id ? { id, mode: selectModeName(state) } : undefined;
  },

  /** @returns whether the value has an element id and a known mode name */
  isEntry({ id, mode }) {
    return (
      typeof id === "string" && typeof mode === "string" && isModeName(mode)
    );
  },

  /**
   * @param state The current state
   * @returns whether the element exists and the mode is a viewing mode that can show it
   */
  canRestore({ id, mode }, state) {
    const element = selectIElement(id)(state);
    const { canBeStartedWith } = getMode(mode);
    return (
      isViewingMode(mode) &&
      !!element &&
      (!canBeStartedWith || canBeStartedWith(element, state))
    );
  },

  /** @returns a thunk switching to the mode if needed, then activating the element */
  restore({ id, mode }) {
    return (dispatch, getState) => {
      const state = getState();
      if (mode !== selectModeName(state)) dispatch(changeMode(mode));
      dispatch(setActiveElement(id));

      // Keeps the element visible, until the scene filter is tracked itself
      const element = selectIElement(id)(state);
      if (!element) return;
      dispatch(
        setCompatibleSceneFilterForMode(
          mode,
          getSceneFiltersForElement(element, state.iElements),
        ),
      );
    };
  },
};
